/**
 * Supabase Client - Configuração centralizada
 * 
 * URL e ANON_KEY centralizados em supabaseEdge.ts para evitar duplicação.
 * Este arquivo apenas reexporta para manter compatibilidade.
 */
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '@/lib/supabaseEdge';

// Re-export para uso em outros lugares
export { SUPABASE_URL, SUPABASE_ANON_KEY };
export const SUPABASE_PUBLISHABLE_KEY = SUPABASE_ANON_KEY;

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// --- HELPERS DE AUTENTICAÇÃO E UNIDADE ---

/**
 * Verifica se há uma sessão ativa autenticada
 */
export const isAuthenticated = async (): Promise<boolean> => {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
};

/**
 * Retorna o ID da unidade operacional do usuário atual
 * (operador_id se vendedor, ou próprio id se operador)
 */
export const getUnidadeId = async (): Promise<number | null> => {
  const { data, error } = await supabase.rpc('get_unidade_operador_id');
  if (error) {
    console.error('[Supabase] Erro ao obter unidade:', error);
    return null;
  }
  return data;
};

// --- DIAGNÓSTICO AVANÇADO DO WEBSOCKET ---

// Função para detectar modo debug (DEV, ?debug=1, ou localStorage)
export const isDebugMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    import.meta.env.DEV ||
    window.location.search.includes('debug=1') ||
    localStorage.getItem('debug_supabase') === '1'
  );
};

// 1. Expor globalmente para testes no Console (DEV ou debug ativo)
if (isDebugMode()) {
  (window as any).supabase = supabase;
  console.log('%c[DEBUG] window.supabase disponível', 'background: #3b82f6; color: white; padding: 2px 6px; border-radius: 4px');
}

// 2. Canal de heartbeat para monitorar status global da conexão WebSocket
if (import.meta.env.DEV) {
  // Criar canal de debug que monitora a conexão
  const debugChannel = supabase.channel('ws-debug-heartbeat');
  
  debugChannel.subscribe((status, err) => {
    if (status === 'SUBSCRIBED') {
      console.log('%c [WS] CONECTADO! 🟢', 'background: #22c55e; color: #fff; font-weight: bold; padding: 2px 6px; border-radius: 4px');
    } else if (status === 'CHANNEL_ERROR') {
      console.log('%c [WS] ERRO 🔴', 'background: #ef4444; color: #fff; font-weight: bold; padding: 2px 6px; border-radius: 4px', err?.message);
    } else if (status === 'TIMED_OUT') {
      console.log('%c [WS] TIMEOUT ⚠️', 'background: #f59e0b; color: #000; font-weight: bold; padding: 2px 6px; border-radius: 4px');
    } else if (status === 'CLOSED') {
      console.log('%c [WS] FECHADO 🔴', 'background: #ef4444; color: #fff; font-weight: bold; padding: 2px 6px; border-radius: 4px');
    } else {
      console.log('[WS] Status:', status);
    }
  });

  console.log('🔌 Supabase Client inicializado (Debug Mode ON):', SUPABASE_URL);
}
